import React, { useEffect, useState } from 'react';
import { Button, Form, Modal } from '@/components/ui';

export interface DataSourceFormField {
  key: 'code' | 'name' | 'type' | 'property';
  label: string;
  required?: boolean;
}

export interface DataSourceFormData {
  code: string;
  name: string;
  type: string;
  property: string;
}

interface DataSourceModalProps {
  visible: boolean;
  title: string;
  fields: DataSourceFormField[];
  initialData?: Partial<DataSourceFormData>;
  onClose: () => void;
  onSave: (data: DataSourceFormData) => Promise<void>;
}

const defaultFormData: DataSourceFormData = {
  code: '',
  name: '',
  type: '',
  property: ''
};

const DataSourceModal: React.FC<DataSourceModalProps> = ({
  visible,
  title,
  fields,
  initialData,
  onClose,
  onSave
}) => {
  const [formData, setFormData] = useState<DataSourceFormData>(defaultFormData);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (visible) {
      setFormData({
        ...defaultFormData,
        ...initialData
      });
    }
  }, [visible, initialData]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setIsSaving(true);
      await onSave({
        code: (formData.code ?? '').trim(),
        name: (formData.name ?? '').trim(),
        type: (formData.type ?? '').trim(),
        property: (formData.property ?? '').trim()
      });
      onClose();
    } catch (error: unknown) {
      console.error('Error saving data source:', error);
      const message =
        error && typeof error === 'object' && 'response' in error && error.response != null
          ? (error.response as { data?: { error?: string } })?.data?.error
          : null;
      alert(message || 'Failed to save data source. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal show={visible} onHide={onClose} backdrop={isSaving ? 'static' : true}>
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      {/* Form wraps Body+Footer (footer holds the submit) and is the flex column itself. */}
      <form onSubmit={handleSubmit} className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col">
        <Modal.Body>
          {fields.map(field => (
            <div className="tw-mb-4" key={field.key}>
              <Form.Label>{field.label}</Form.Label>
              <Form.Control
                type="text"
                name={field.key}
                value={formData[field.key]}
                onChange={handleChange}
                required={field.required}
              />
            </div>
          ))}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={isSaving}>
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
        </Modal.Footer>
      </form>
    </Modal>
  );
};

export default DataSourceModal;
